import { useCallback, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../backend/convex/_generated/api';
import { Id } from '../../../backend/convex/_generated/dataModel';
import { posthog } from '@/utils/posthog';
import { Chapter } from '@/types';

interface UnlockUser {
  id: string;
  tokenBalance: number;
}

interface UseChapterUnlockArgs {
  user: UnlockUser | null;
  signIn: () => void;
  bookId?: string;
  book?: { _id?: Id<'books'> | string; title?: string } | null;
  refreshBalance: () => void;
}

// Track in-flight unlock requests to prevent double-spending
const pendingUnlocks = new Set<string>();

export function useChapterUnlock({
  user,
  signIn,
  bookId,
  book,
  refreshBalance,
}: UseChapterUnlockArgs) {
  const unlockChapter = useMutation(api.chapters.unlock);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [optimisticallyUnlockedChapters, setOptimisticallyUnlockedChapters] = useState<Set<string>>(new Set());

  const rollbackOptimisticUnlock = useCallback((chapterId: string) => {
    setOptimisticallyUnlockedChapters((prev) => {
      const next = new Set(prev);
      next.delete(chapterId);
      return next;
    });
  }, []);

  const handleUnlockChapter = useCallback(async (chapter: Chapter) => {
    if (!user) {
      signIn();
      return false;
    }

    const unlockKey = `${user.id}-${chapter._id}`;
    if (pendingUnlocks.has(unlockKey)) {
      return false;
    }

    pendingUnlocks.add(unlockKey);
    setOptimisticallyUnlockedChapters((prev) => new Set(prev).add(chapter._id));
    setErrorMessage(null);

    try {
      const result = await unlockChapter({
        id: chapter._id as Id<'chapters'>,
        clerkId: user.id,
      });

      posthog.capture('chapter_unlocked', {
        book_id: bookId,
        book_title: book?.title,
        chapter_id: chapter._id,
        chapter_sequence: chapter.sequence,
        chapter_title: chapter.title,
        token_cost: chapter.tokenCost,
        token_balance_remaining: result?.remainingBalance,
      });

      refreshBalance();
      return true;
    } catch (error) {
      rollbackOptimisticUnlock(chapter._id);
      const message = error instanceof Error ? error.message : String(error);

      if (message.includes('Insufficient tokens')) {
        setErrorMessage(`insufficient_tokens:${user.tokenBalance ?? 0}:${chapter.tokenCost}`);
      } else {
        setErrorMessage('Failed to unlock chapter. Please try again.');
      }

      return false;
    } finally {
      pendingUnlocks.delete(unlockKey);
    }
  }, [user, signIn, unlockChapter, bookId, book?.title, refreshBalance, rollbackOptimisticUnlock]);

  return {
    errorMessage,
    setErrorMessage,
    optimisticallyUnlockedChapters,
    handleUnlockChapter,
  };
}
